import { Languages } from '#lib/enum.js';
import { publishedGuidePagePaths } from '#marketing/lib/guide-registry.js';

/** @type {readonly ['en', 'fr', 'zh']} */
export const MARKETING_LOCALES = [Languages.English, Languages.French, Languages.Chinese];

/** @type {'en'} */
export const MARKETING_DEFAULT_LOCALE = Languages.English;

/** Locales that use a URL prefix (English stays at root). @type {readonly ['fr', 'zh']} */
export const MARKETING_PREFIXED_LOCALES = [Languages.French, Languages.Chinese];

/** Endonym labels for the language switcher. */
export const MARKETING_LOCALE_LABELS = {
  en: 'English',
  fr: 'Français',
  zh: '中文 (简体)',
};

/** Logical marketing pages (locale-neutral paths). */
export const MARKETING_PAGE_PATHS = [
  '/',
  '/features',
  '/how-it-works',
  '/guides',
  '/about',
  '/faq',
  '/contact',
  '/privacy',
];

/**
 * @param {string} lang
 * @returns {lang is 'en' | 'fr' | 'zh'}
 */
export function isValidMarketingLocale(lang) {
  return MARKETING_LOCALES.includes(lang);
}

/**
 * Parse locale and locale-neutral path from a pathname.
 *
 * @param {string} pathname
 * @returns {{ locale: string, pathWithoutLocale: string }}
 */
export function parseLocaleFromPath(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  const first = segments[0];

  if (first && isValidMarketingLocale(first)) {
    const rest = segments.slice(1);
    const pathWithoutLocale = rest.length === 0 ? '/' : `/${rest.join('/')}`;
    return { locale: first, pathWithoutLocale };
  }

  return {
    locale: MARKETING_DEFAULT_LOCALE,
    pathWithoutLocale: pathname === '' ? '/' : pathname,
  };
}

/**
 * Build a locale-aware marketing URL path.
 *
 * @param {string} locale
 * @param {string} path - Locale-neutral path (e.g. `/features`)
 * @returns {string}
 */
export function localePath(locale, path) {
  const normalized = path.startsWith('/') ? path : `/${path}`;

  if (locale === MARKETING_DEFAULT_LOCALE) {
    return normalized;
  }

  if (normalized === '/') {
    return `/${locale}`;
  }

  return `/${locale}${normalized}`;
}

/**
 * Map marketing locale to `<html lang>` (Chinese uses BCP 47 zh-Hans).
 *
 * @param {string} locale
 * @returns {string}
 */
export function marketingHtmlLang(locale) {
  if (locale === Languages.Chinese) {
    return 'zh-Hans';
  }
  return locale;
}

/**
 * Resolve marketing locale from `<html lang>`.
 *
 * @param {string} htmlLang
 * @returns {string}
 */
export function marketingLocaleFromHtmlLang(htmlLang) {
  if (htmlLang === 'zh-Hans' || htmlLang === 'zh') {
    return Languages.Chinese;
  }
  if (isValidMarketingLocale(htmlLang)) {
    return htmlLang;
  }
  return MARKETING_DEFAULT_LOCALE;
}

/**
 * Locale-neutral pages for prerender and sitemap, including published /guides/:id.
 *
 * @returns {string[]}
 */
export function marketingSitemapPagePaths() {
  return [...MARKETING_PAGE_PATHS, ...publishedGuidePagePaths()];
}

/**
 * All prerender paths for the marketing build (English root + prefixed locales).
 *
 * @returns {string[]}
 */
export function marketingPrerenderPaths() {
  const pages = marketingSitemapPagePaths();
  /** @type {string[]} */
  const paths = [...pages];

  for (const locale of MARKETING_PREFIXED_LOCALES) {
    for (const page of pages) {
      paths.push(localePath(locale, page));
    }
  }

  return paths;
}

/**
 * Cloudflare Turnstile language code for a marketing locale.
 *
 * @param {string} locale
 * @returns {string}
 */
export function marketingTurnstileLanguage(locale) {
  if (locale === Languages.French) {
    return 'fr';
  }
  if (locale === Languages.Chinese) {
    return 'zh-cn';
  }
  return 'en';
}
